import type { ComponentType, ReactElement, ReactNode } from 'react'
import { CardContent } from '@/components/ui/card'
import { Typography } from '@/components/ui/typography'
import { cn } from '@/lib/utils'
import { OrderAsidePanelCard } from './OrderAsidePanelCard'
import { ORDER_ASIDE_SUBTLE_SURFACE_CLASS } from './order-aside.styles'

export const DETAIL_PANEL_TONES = {
  slate: {
    icon: 'border-border bg-elevated text-muted-foreground',
  },
  brand: {
    icon: 'border-primary bg-accentSecondary text-interaction dark:border-primary dark:bg-accentSecondary dark:text-interaction',
  },
  info: {
    icon: 'border-status-info-border bg-status-info-background text-status-info-foreground',
  },
  warning: {
    icon: 'border-status-warning-border bg-status-warning-background text-status-warning-foreground',
  },
  destructive: {
    icon: 'border-status-destructive-border bg-status-destructive-background text-status-destructive-foreground',
  },
  success: {
    icon: 'border-status-success-border bg-status-success-background text-status-success-foreground',
  },
  amethyst: {
    icon: 'border-chart-1/40 bg-accentSecondary text-interaction',
  },
  periwinkle: {
    icon: 'border-chart-5/40 bg-accent text-status-info-foreground',
  },
  mint: {
    icon: 'border-chart-3/40 bg-brand-mint text-status-success-foreground',
  },
}

interface OrderPanelHeaderProps {
  title?: ReactNode
  subtitle?: ReactNode
  icon: ComponentType<{ className?: string }>
  tone?: keyof typeof DETAIL_PANEL_TONES | 'neutral'
  action?: ReactNode
}

export function OrderAsidePanelHeader({
  title,
  subtitle,
  icon: Icon,
  tone = 'neutral',
  action = null,
}: OrderPanelHeaderProps): ReactElement {
  const toneClasses =
    DETAIL_PANEL_TONES[tone === 'neutral' ? 'slate' : tone] ?? DETAIL_PANEL_TONES.slate

  return (
    <div className="flex items-start justify-between gap-3">
      <div className={cn('flex min-w-0 gap-3', subtitle ? 'items-start' : 'items-center')}>
        <div
          className={cn(
            'flex size-10 shrink-0 items-center justify-center rounded-xl border',
            toneClasses.icon
          )}
        >
          <Icon className="size-5" />
        </div>
        <div className="min-w-0">
          <Typography variant="eyebrow">{title}</Typography>
          {subtitle ? (
            <Typography variant="supporting" className="mt-1 block">
              {subtitle}
            </Typography>
          ) : null}
        </div>
      </div>
      {action}
    </div>
  )
}

export function OrderDetailPanel({
  title,
  subtitle,
  icon: Icon,
  tone = 'neutral',
  action = null,
  className,
  children,
}: OrderPanelHeaderProps & { className?: string; children?: ReactNode }): ReactElement {
  const accent = tone === 'neutral' || tone === 'slate' ? 'neutral' : tone

  return (
    <OrderAsidePanelCard accent={accent} className={cn('gap-0 overflow-hidden py-0', className)}>
      <CardContent className="space-y-4 px-5 py-5 sm:px-6 sm:py-6">
        <OrderAsidePanelHeader
          title={title}
          subtitle={subtitle}
          icon={Icon}
          tone={tone}
          action={action}
        />
        {children}
      </CardContent>
    </OrderAsidePanelCard>
  )
}

export function OrderDetailValueRow({
  icon: Icon,
  value,
  emptyLabel = '(None provided)',
  copyAction = null,
}: {
  icon: ComponentType<{ className?: string }>
  value?: ReactNode
  emptyLabel?: ReactNode
  copyAction?: ReactNode
}): ReactElement {
  return (
    <div
      className={cn('flex min-h-14 items-center gap-3 px-3 py-2', ORDER_ASIDE_SUBTLE_SURFACE_CLASS)}
    >
      <Icon className="size-4 shrink-0 text-muted-foreground" />
      <div className="min-w-0 flex-1">
        {value ? (
          <span className="block truncate text-sm">{value}</span>
        ) : (
          <Typography variant="supporting" italic>
            {emptyLabel}
          </Typography>
        )}
      </div>
      {copyAction}
    </div>
  )
}
